function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useRef, useLayoutEffect, useState } from 'react';
import { createAssignSharedRefs } from 'utilities/input.helpers';
import { FormContextProvider, useFormContext } from '../FormContext';
import * as S from './Fieldset.styles';
const Fieldset = /*#__PURE__*/React.forwardRef(({
  children,
  legend,
  orientation,
  header,
  hasHorizontalRule,
  hasGutters = true,
  ...fieldsetProps
}, ref) => {
  const [longestLabelWidth, setLongestLabelWidth] = useState(0);
  const internalRef = useRef(null);
  const context = useFormContext();
  if (!context) {
    console.warn('Avoid rendering a `Fieldset` outside a `Form`');
  }

  // Since the label size varies here we are measuring the wider label. This value will be used to set the width of
  // `InputHeaderLeftContainer` on horizontal inputs and inside Checkboxes and Switches that needs to be rendered aligned with the inputs not the labels.
  useLayoutEffect(() => {
    const self = internalRef.current;
    if (self && (orientation === 'horizontal' || context?.orientation === 'horizontal')) {
      const horizontalLabels = self.querySelectorAll('div > label[data-horizontal=true]');
      const labelSizes = [...horizontalLabels].map(node => node.clientWidth);
      const longestLabelWidth = Math.max(...labelSizes);
      setLongestLabelWidth(longestLabelWidth);
    }
  }, [context, orientation]);
  const assignSharedRefs = createAssignSharedRefs(ref, internalRef);
  const getContextProps = () => {
    if (orientation === 'horizontal' || context.orientation === 'horizontal') {
      return {
        orientation: 'horizontal',
        inputSize: 'medium'
      };
    }
    return {
      orientation: orientation ?? context?.orientation,
      inputSize: fieldsetProps.inputSize ?? context?.inputSize
    };
  };
  const contextProps = getContextProps();
  return /*#__PURE__*/React.createElement(FormContextProvider, contextProps, /*#__PURE__*/React.createElement(S.FieldsetContainer, _extends({
    ref: assignSharedRefs
  }, fieldsetProps, {
    $hasHorizontalRule: hasHorizontalRule,
    $size: context?.inputSize,
    $hasGutters: hasGutters
  }), header ? /*#__PURE__*/React.createElement(S.FieldsetHeader, {
    customTag: "h2",
    variant: "ui",
    size: "large",
    weight: "bold",
    $size: context?.inputSize,
    $hasHorizontalRule: hasHorizontalRule
  }, header) : null, legend, /*#__PURE__*/React.createElement(S.FieldsetControlsContainer, {
    $isHorizontal: orientation === 'horizontal',
    $size: context?.inputSize
  }, React.Children.map(children, child => {
    if (! /*#__PURE__*/React.isValidElement(child)) {
      return child;
    }

    // Within InputControl context it is the width of the left container, within checkboxes and switches it is the main container left margin.
    const props = {
      marginLeft: longestLabelWidth,
      leftContainerWidth: longestLabelWidth
    };
    return /*#__PURE__*/React.cloneElement(child, props);
  }))));
});
export { Fieldset };